import { useEffect } from 'react';

import { ExportOutlined, FileOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Form, Popconfirm, Switch, Tag } from 'antd';
import dayjs from 'dayjs';
import { OFFER_STATUS } from 'utils';

import { stripGroupPrefix } from '../strip-group-prefix';

import { findMainMember } from './main-member';
import { mutedNoteStyle, mutedTextStyle } from './muted-text-style';
import { getShareIdentificationBlocker } from './share-identification';

/** Salt okunur kart dosyası satırı; tıklanınca dosya yeni sekmede açılır. */
function CardFileLink({ file, caption }) {
  return (
    <a
      href={file.fileFullUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="gx-d-flex gx-align-items-center gx-border"
      style={{ gap: 8, padding: '6px 10px', borderRadius: 6, backgroundColor: 'white' }}
    >
      <FileOutlined />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file.name}
      </span>
      <span style={mutedNoteStyle}>{caption}</span>
    </a>
  );
}

/**
 * Ana maddenin yayılacak kartı. Aramadan eklenen satır henüz grubun üyesi olmadığı için (`memberId` yok) kartı
 * Kaydet'ten önce bilinmez; kart yoksa BE Kaydet'te reddeder.
 */
function MainMemberCard({ mainMember, groupName }) {
  if (!mainMember.memberId) {
    return <span style={mutedTextStyle}>Kart, Kaydet sırasında kontrol edilir.</span>;
  }

  const media = mainMember.identificationCard?.media ?? [];
  const mainMemberTitle = stripGroupPrefix(mainMember.label ?? '', groupName);

  return media.map((file) => (
    <CardFileLink key={file.id ?? file.fileFullUrl} file={file} caption={mainMemberTitle} />
  ));
}

/**
 * Kurulu ortaklaştırmanın kartı: son güncellenme tarihi ve yüklendiği madde. Kart herhangi bir üyeden
 * güncellenebildiği için bu madde ana maddeden farklı olabilir; eski kartlarda madde bilinmez.
 */
function SharedCard({ card, groupName }) {
  const uploadedFromTitle = card.uploadedFromOffer?.title;

  return (card.media ?? []).map((file) => {
    const caption = [
      file.uploadedAt && dayjs(file.uploadedAt).format('DD.MM.YYYY'),
      uploadedFromTitle && stripGroupPrefix(uploadedFromTitle, groupName),
    ]
      .filter(Boolean)
      .join(' · ');

    return <CardFileLink key={file.id ?? file.fileFullUrl} file={file} caption={caption} />;
  });
}

/**
 * Ortaklaştırmanın neden açılamadığı: geride kalan maddeler statüleriyle ya da ana maddenin eksik kartı.
 */
function ShareBlockedNotice({ blocker, groupName }) {
  return (
    <div
      className="gx-d-flex gx-align-items-start gx-border"
      style={{ gap: 8, padding: '8px 12px', borderRadius: 6, fontSize: 13, backgroundColor: '#F7F8F7' }}
    >
      <InfoCircleOutlined style={{ marginTop: 4 }} />
      {blocker.pendingMembers ? (
        <div>
          Ortaklaştırma için gruptaki bütün maddelerin Telif Sonrası aşamasında olması gerekiyor. Henüz geçmeyenler:
          <ul className="gx-d-flex gx-flex-column" style={{ gap: 4, margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
            {blocker.pendingMembers.map((member) => (
              <li key={member.id} className="gx-d-flex gx-align-items-center" style={{ gap: 8 }}>
                <a href={`/madde-detay/${member.id}`} target="_blank" rel="noopener noreferrer" title="Yeni sekmede aç">
                  <ExportOutlined />
                </a>
                <span>
                  {member.label}
                  {OFFER_STATUS[member.status] && <span style={mutedTextStyle}> · {OFFER_STATUS[member.status]}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <span>
          Ana maddeye ({stripGroupPrefix(blocker.mainMemberWithoutCard.label ?? '', groupName)}) henüz kimlik kartı
          yüklenmedi.
        </span>
      )}
    </div>
  );
}

/**
 * Ortaklaştırma anahtarı. Kurulu ortaklaştırmayı kapatmak sonraki yüklemelerin davranışını değiştirdiği ve tekrar
 * açmak şarta bağlı olduğu için `confirmClose` iken kapatma önce onay ister; onay yalnız form değerini değiştirir,
 * istek Kaydet'le gider.
 */
function ShareSwitch({ checked, onChange, disabled, confirmClose }) {
  if (!confirmClose) {
    return <Switch checked={checked} onChange={onChange} disabled={disabled} />;
  }

  return (
    <Popconfirm
      placement="bottomRight"
      overlayStyle={{ maxWidth: 380 }}
      title={
        <div className="gx-d-flex gx-flex-column" style={{ gap: 8 }}>
          <b>Ortaklaştırma kaldırılsın mı?</b>
          <span>
            Maddelerin mevcut kimlik kartları olduğu gibi kalır. Bundan sonra bir maddeye yüklenen kart yalnız o
            maddeye yazılır.
          </span>
          <span>
            Tekrar açmak için gruptaki bütün maddelerin Telif Sonrası'nda olması gerekir. Açıldığında ana maddenin kartı
            bütün maddelere yeniden yazılır.
          </span>
        </div>
      }
      okText="Kaldır"
      cancelText="Vazgeç"
      // Tema birincil düğmenin rengini `!important` ile sabitliyor; kırmızı yalnız default tipte görünür.
      okButtonProps={{ type: 'default', danger: true }}
      onConfirm={() => onChange(false)}
    >
      {/* Değer yalnız onayla değişir; tıklama anahtarı çevirmez, onay kutusunu açar. */}
      <Switch checked={checked} />
    </Popconfirm>
  );
}

/**
 * "Kimlik kartını ortaklaştır" paneli. Anahtar yalnız form değerini değiştirir; ortaklaştırma grup Kaydet'iyle
 * birlikte gönderilir.
 * Şart sağlanmıyorsa anahtar açılamaz; açık duran anahtar ise kapatılabilir kalır ki kullanıcı ortaklaştırmayı
 * kaldırıp grubu düzenleyebilsin. Kullanıcının bu pencerede açtığı anahtar şart bozulunca kapanır ki Kaydet
 * BE'nin reddedeceği bir ortaklaştırma göndermesin. Kurulu ortaklaştırmaya dokunulmaz.
 * Grup zaten ortaksa ve anahtar açık duruyorsa yayılacak kart yerine kurulu ortak kart gösterilir; anahtar
 * başlangıç değerinde olduğu için Kaydet ortaklaştırmayı yeniden göndermez.
 * @param {Object} props
 * @param {Object} props.form - Antd Form instance; `members` ve `shareIdentification` alanları izlenir
 * @param {boolean} props.initialShareIdentification - Grubun kayıtlı ortaklaştırma durumu
 * @param {Object|null} props.sharedIdentificationCard - Kurulu ortaklaştırmanın kartı; grup ortak değilse `null`
 */
function ShareIdentificationPanel({ form, initialShareIdentification, sharedIdentificationCard }) {
  const members = Form.useWatch('members', form) ?? [];
  const groupName = Form.useWatch('name', form);
  const isSharing = Boolean(Form.useWatch('shareIdentification', form));

  const mainMember = findMainMember(members);
  const blocker = getShareIdentificationBlocker(members, mainMember);
  const isBlocked = Boolean(blocker);
  const isEstablished = Boolean(sharedIdentificationCard) && isSharing;

  useEffect(() => {
    if (isBlocked && isSharing && !initialShareIdentification) {
      form.setFieldsValue({ shareIdentification: false });
    }
  }, [isBlocked, isSharing, initialShareIdentification, form]);

  return (
    <div
      className="gx-d-flex gx-flex-column gx-border"
      style={{ gap: 10, padding: '12px 14px', borderRadius: 6, backgroundColor: '#FCFDFC' }}
    >
      <div className="gx-d-flex gx-align-items-center gx-justify-content-between">
        <span style={{ flex: 1, fontWeight: 600, color: isBlocked ? '#B4BCB8' : undefined }}>
          Kimlik kartını ortaklaştır
        </span>
        {isEstablished && <Tag color="blue">Ortak</Tag>}
        <Form.Item name="shareIdentification" valuePropName="checked" noStyle>
          <ShareSwitch disabled={isBlocked && !isSharing} confirmClose={isEstablished} />
        </Form.Item>
      </div>

      {isBlocked && <ShareBlockedNotice blocker={blocker} groupName={groupName} />}

      {!isBlocked && !isSharing && (
        <span style={mutedTextStyle}>Açarsanız ana maddenin kimlik kartı gruptaki tüm maddelere yazılır.</span>
      )}

      {isEstablished && (
        <>
          <SharedCard card={sharedIdentificationCard} groupName={groupName} />
          <span style={mutedTextStyle}>
            Kartı gruptaki herhangi bir maddenin dosya yükleme ekranından güncelleyebilirsiniz; değişiklik tüm maddelere
            yansır. Gruptan çıkarılan madde mevcut kartını korur.
          </span>
        </>
      )}

      {!isBlocked && isSharing && !isEstablished && (
        <>
          {mainMember && <MainMemberCard mainMember={mainMember} groupName={groupName} />}
          <div
            className="gx-d-flex"
            style={{
              gap: 8,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
              color: '#176494',
              backgroundColor: '#EAF5FC',
              border: '1px solid #BEE0F5',
            }}
          >
            <InfoCircleOutlined style={{ marginTop: 4 }} />
            <span>
              Kaydettiğinizde bu kart gruptaki <b>{members.length} maddenin</b> tamamına yazılır. Maddelerin şu anki
              kartlarının yerini alır.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default ShareIdentificationPanel;
